import React, { MouseEvent, useCallback } from 'react';
import styles from './MultiUpload.module.scss'
import { useDropzone } from 'react-dropzone';
import { closestCenter, DndContext, MouseSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import { v4 as uuid_v4 } from 'uuid';
import Button from "@/Atoms/Input/Button";
import Thumbnail from "./Thumbnail";
import { Media } from "@/types/Media";
import Error from "@/Atoms/Messages/Error";

type UploadMediaType = Media & {
  progress?: number,
  status?: 'pending' | 'uploading' | 'error';
}

export default (
  {
    items,
    error,
    onChange,
    onUpload,
    onDelete,
    onReorder,
    allowPdf,
    acceptedFileTypes,
    limitLabel,
  }: {
    items: UploadMediaType[]
    error?: string
    onChange: (items: UploadMediaType[] | ((prev: UploadMediaType[]) => UploadMediaType[])) => void;
    onUpload: (file: File, id: string, index: number) => Promise<any>,
    onDelete: (file: UploadMediaType, index: number) => Promise<any>,
    onReorder: (oldIndex: number, newIndex: number) => Promise<any>,
    allowPdf?: boolean
    acceptedFileTypes?: { [mimeType: string]: string[] }
    /**
     * shown instead of the number of items, when the limits are checked by the parent
     */
    limitLabel?: string
  }
) => {

  const maximumPhotos = 10;

  const getFileIcon = (mimeType: string): string | null => {
    const iconMap: { [key: string]: string } = {
      'application/pdf': '/static/icons/pdf-file.svg',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '/static/icons/doc-file.svg',
      'application/msword': '/static/icons/doc-file.svg',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '/static/icons/xls-file.svg',
      'application/vnd.ms-excel': '/static/icons/xls-file.svg',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '/static/icons/ppt-file.svg',
      'application/vnd.ms-powerpoint': '/static/icons/ppt-file.svg',
    };
    return iconMap[mimeType] || null;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {

    onChange((prev: UploadMediaType[]) => {

      const allowedNewFiles = Math.max(maximumPhotos - prev.length, 0);

      const newItems = acceptedFiles
      .slice(0, allowedNewFiles)
      .map((file, index): UploadMediaType => {
        const generatedId = uuid_v4();

        onUpload(file, generatedId, index)

        let url = null;
        const fileIcon = getFileIcon(file.type);
        if (fileIcon) {
          url = fileIcon;
        } else if (file.type.startsWith('image/')) {
          url = URL.createObjectURL(file);
        } else {
          // Generic file icon for unknown types
          url = '/static/icons/file.svg';
        }

        return {
          id: generatedId,
          progress: 0.1,// starting
          status: 'pending',
          url: url,
        };
      })
      return [...prev, ...newItems]
    });

  }, [onChange, onUpload]);

  const {getRootProps, getInputProps, isDragActive, open: handleUploadOpen} = useDropzone({
    accept: acceptedFileTypes || {
      'image/jpeg': [],
      'image/png': [],
      // 'image/gif': [],
      ...(allowPdf && {'application/pdf': []}),
    },
    onDrop,
    noClick: true, // prevent clickable area
    noKeyboard: true, // prevent space/enter
  });

  const handleDeleteImage = async (e: MouseEvent<HTMLElement>, index: number) => {
    e.stopPropagation();

    await onDelete(items[index], index)
  }

  const sensors = useSensors(useSensor(MouseSensor));

  const handleDragEnd = async (event: any) => {
    const {active, over} = event;

    if (active.id == over?.id) {
      return;
    }
    const oldIndex = items.findIndex(img => img.id === active.id);
    const newIndex = items.findIndex(img => img.id === over?.id);
    await onReorder(oldIndex, newIndex);
  };

  return (
    <div className={styles.container}>
      <div
        {...getRootProps()}
        className={`${styles.photos} ${styles.dropzone}`}
      >
        <div>
          <i>{limitLabel ?? (items.length === 0 ? `maximaal ${maximumPhotos}` : `${items.length} / ${maximumPhotos}`)}</i>
        </div>
        <input {...getInputProps()} />

        {isDragActive ?
          <p>Laat los om de bestanden te plaatsen...</p> :
          <p className={'pb-2'}>
            Sleep hier je {acceptedFileTypes ? 'bestanden' : `afbeeldingen${allowPdf ? ' of pdf' : ''}`}, of <Button
            size={"tiny"}
            onClick={handleUploadOpen}
          >
            klik om te uploaden
          </Button>
          </p>
        }

        {error && <Error show={true}>
          {error}
        </Error>}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToParentElement]}

        >
          <SortableContext
            items={items.map(file => file.id)}
            strategy={rectSortingStrategy}
          >
            <div className="flex flex-wrap gap-4">
              {items.map((file, index) => {
                // Fall back to a static file-type icon whenever we know the mimetype isn't an image.
                const fileIcon = file.mimetype ? getFileIcon(file.mimetype) : null;
                const src = fileIcon ?? file.url;

                return (
                  <Thumbnail
                    key={file.id}
                    id={file.id}
                    src={src}
                    progress={file.progress}
                    onDelete={(e) => handleDeleteImage(e, index)}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
